import { useParams } from "next/navigation";
import CenterStageIcon from "@/assets/logos/center-stage.svg";
import AnchorIf from "@/components/common/AnchorIf";
import Picture from "@/components/common/Picture";
import TextField from "@/components/site-builder/TextField";
import { CENTER_STAGE_PAGE_URL } from "@/constants";
import { TextAlignment, TitleSize } from "@/constants/content";
import { AspectRatio, ImageSize, MediaPosition } from "@/constants/media";
import {
  BasePage,
  SiteBuilderModuleTextFieldType,
} from "@/constants/site-builder";
import { ColorVariant, FontVariant } from "@/constants/theme";
import { createThemeColorObject } from "@/utils/theme/createThemeColorObject";
import { createSiteBasePageURL } from "@/utils/url/createSiteBasePageURL";
import { createSitePathURL } from "@/utils/url/createSitePathURL";
import type { Speaker } from "../types";
import type { ExtendedUploadedImage } from "@/types";
import type {
  SiteBuilderModuleHeaderField,
  SiteBuilderModuleTextField,
} from "@/types/site-builder";
import s from "./SpeakerVariant1.module.scss";

export interface SpeakerVariant1Props {
  speaker: Speaker;
  imageSettings: ExtendedUploadedImage;
  showCompanyLogo: boolean;
  disableSpeakerCardUrl: boolean;
}

export default function SpeakerVariant1(props: SpeakerVariant1Props) {
  const { speaker, imageSettings, showCompanyLogo, disableSpeakerCardUrl } =
    props;
  const {
    forename,
    surname,
    middlename,
    company,
    jobTitle,
    photo,
    companyLogo,
    centerStageMember,
  } = speaker;

  const { sitePath } = useParams<{ sitePath: string }>();

  const photoImage = {
    ...imageSettings,
    source: { path: photo?.path },
  };

  const companyLogoImage = {
    aspectRatio: AspectRatio["Auto"],
    size: ImageSize.Cover,
    position: MediaPosition.Center,
    source: { path: companyLogo?.path },
  };

  const fullNameTextField: SiteBuilderModuleHeaderField = {
    type: SiteBuilderModuleTextFieldType.Header,
    text: [forename, middlename, surname].filter(Boolean).join(" "),
    tag: TitleSize.H3,
    alignment: TextAlignment.Left,
    fontVariant: FontVariant.Default,
    color: createThemeColorObject(ColorVariant.Inherit),
  };

  const jobTitleTextField: SiteBuilderModuleTextField = {
    type: SiteBuilderModuleTextFieldType.Text,
    text: jobTitle,
    alignment: TextAlignment.Left,
    fontVariant: FontVariant.Default,
    color: createThemeColorObject(ColorVariant.Inherit),
  };

  const companyTextField: SiteBuilderModuleTextField = {
    type: SiteBuilderModuleTextFieldType.Text,
    text: company,
    alignment: TextAlignment.Left,
    fontVariant: FontVariant.Default,
    color: createThemeColorObject(ColorVariant.Inherit),
  };

  const speakerPath = createSiteBasePageURL(BasePage.Speakers, speaker.path);
  const speakerUrl = createSitePathURL(sitePath, speakerPath);

  const getContent = () => {
    return (
      <>
        <Picture imageConfig={photoImage} className={s.photo} />
        <div className={s.details}>
          <TextField
            field={fullNameTextField}
            className={s.text}
            type="header-h4"
          />
          <TextField
            field={jobTitleTextField}
            className={s.text}
            type="text-large"
          />
          <TextField
            field={companyTextField}
            className={s.text}
            type="text-large"
          />
          {showCompanyLogo && <Picture imageConfig={companyLogoImage} />}
        </div>
      </>
    );
  };

  return (
    <div className={s.block}>
      <AnchorIf
        openInNewTab
        href={centerStageMember ? CENTER_STAGE_PAGE_URL : ""}
        className={s.cornerIcon}
      >
        {centerStageMember && <CenterStageIcon className={s.icon} />}
      </AnchorIf>

      <AnchorIf
        href={!disableSpeakerCardUrl ? speakerUrl : ""}
        className={s.inner}
      >
        {getContent()}
      </AnchorIf>
    </div>
  );
}
